import { createFileRoute } from "@tanstack/react-router";
import { useBreadCrumbs } from "~/contexts/useBreadCrumbs";
import {
  getKnowledgeBaseDocBySlug,
  normalizeKnowledgeBaseSlug,
} from "~/lib/knowledge-base";
import {
  KnowledgeBaseLayout,
  KnowledgeBaseNotFound,
} from "~/components/knowledge-base/knowledge-base-layout";
import { KnowledgeBaseMarkdown } from "~/components/knowledge-base/knowledge-base-markdown";

export const Route = createFileRoute("/_dashboard/$datasetSlug/docs/$")({
  component: RouteComponent,
  loader: async ({ params }) => {
    const slug = normalizeKnowledgeBaseSlug(params._splat);
    const doc = getKnowledgeBaseDocBySlug(slug);

    if (!doc) {
      return { slug, doc: null, content: null, status: "not-found" as const };
    }

    try {
      const response = await fetch(doc.publicPath);
      if (!response.ok) {
        throw new Error(`Failed to load ${doc.publicPath}`);
      }

      const content = await response.text();
      return { slug, doc, content, status: "ready" as const };
    } catch {
      return { slug, doc, content: null, status: "error" as const };
    }
  },
});

function RouteComponent() {
  const { datasetSlug } = Route.useParams();
  const { slug, doc, content, status } = Route.useLoaderData();

  useBreadCrumbs([
    { title: "Knowledge Base", link: `/${datasetSlug}/docs` },
    { title: doc?.title ?? "Not Found", link: `/${datasetSlug}/docs/${slug}` },
  ]);

  return (
    <KnowledgeBaseLayout
      datasetSlug={datasetSlug}
      activeSlug={doc?.slug}
      title={doc?.title ?? "Knowledge Base"}
      description={doc?.summary ?? "Read internal Quake documentation directly inside the dashboard."}
    >
      {status !== "ready" || !doc || !content ? (
        <KnowledgeBaseNotFound datasetSlug={datasetSlug} slug={slug} />
      ) : (
        <article className="mx-auto w-full max-w-4xl space-y-6">
          <div className="space-y-2 border-b border-border/70 pb-5">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">{doc.section}</div>
            <p className="text-sm leading-6 text-muted-foreground">{doc.summary}</p>
          </div>
          <div className="space-y-5">
            <KnowledgeBaseMarkdown content={content} />
          </div>
        </article>
      )}
    </KnowledgeBaseLayout>
  );
}
